import { Component, OnInit } from '@angular/core';
import { roofAzimuthConst, roofTiltValuesConst } from '../../common/calculator-constants';
import { AbstractCalculatorComponentDirective } from '../abstract-calculator-component.directive';
import { FormArray, FormControl, Validators } from '@angular/forms';
import { ParseProduction } from '../../common/data-file-parsers/parse-production';
import {
  ParseProductionImplementationPvSys,
} from '../../common/data-file-parsers/parse-production-implementation-pv-sys';
import {
  ParseProductionImplementationPvSol,
} from '../../common/data-file-parsers/parse-production-implementation-pv-sol';
import { SiteDataInputType } from '../../calculator.component';
import { SelectOption } from '../../../../inputs/select/model/select-option';
import { inOutAnimation } from '../../../../animations';
import { UserRole } from '../../../../models/user/user-role.enum';

@Component({
  selector: 'psc-calculator-site-data-inputs',
  templateUrl: './calculator-site-data-inputs.component.html',
  animations: [
    inOutAnimation({ enterDelay: 0.5 }),
  ],
})
export class CalculatorSiteDataInputsComponent extends AbstractCalculatorComponentDirective implements OnInit {

  roofTiltValues = roofTiltValuesConst;
  roofAzimuthValues = roofAzimuthConst;

  siteDataTypeOptions: SelectOption[] = [
    { label: 'PVGIS', value: SiteDataInputType.PVGIS },
    { label: 'NREL', value: SiteDataInputType.NREL },
    { label: 'PVsyst', value: SiteDataInputType.PVSYS },
    { label: 'PV*SOL', value: SiteDataInputType.PVSOL },
  ];

  SiteDataInputType: typeof SiteDataInputType = SiteDataInputType;
  protected readonly UserRole = UserRole;

  get consumption(): FormArray {
    return this.formGroup.controls['consumption'] as FormArray;
  }


  ngOnInit() {

    this.formGroup.addControl('site_data_type', new FormControl('', Validators.required));
    if (!this.authService.userHasRoles([UserRole.ROLE_BUSINESS, UserRole.ROLE_BUSINESS_PLUS])) {
      this.formGroup.controls['site_data_type'].setValue(SiteDataInputType.PVGIS);
      this.addSiteDataUserInputControls();
    }

    this.subs.add(
      this.formGroup.controls['site_data_type']?.valueChanges.subscribe((value) => {
        this.siteDataTypeChanged(value);
      }),
    );

    this.handleProductionFileChange();
  }

  private siteDataTypeChanged(value: SiteDataInputType) {
    if (!value) {
      return;
    }
    if (value === SiteDataInputType.NREL || value === SiteDataInputType.PVGIS) {

      this.formGroup.controls['site_data_file'].removeValidators(Validators.required);
      this.addSiteDataUserInputControls();
      this.formGroup.removeControl('system_capacity');
    } else {

      this.formGroup.controls['site_data_file'].addValidators(Validators.required);
      this.formGroup.removeControl('tilt');
      this.formGroup.removeControl('azimuth');
      this.formGroup.removeControl('area');
      this.formGroup.removeControl('location');
      this.formGroup.addControl('system_capacity', new FormControl('', [Validators.required, Validators.min(0)]));
    }
    this.formGroup.updateValueAndValidity();
  }

  handleProductionFileChange() {
    this.subs.sink = this.formGroup.controls['site_data_file']?.valueChanges.subscribe((files: File[]) => {
      if (!files || files.length === 0) {
        return;
      }
      const csvFile = files[0];
      if (this.formGroup.controls['site_data_file'].errors?.['fileType'] || this.formGroup.controls['site_data_file'].errors?.['fileSize']) {
        return;
      }
      this.loadingService.showLoader();
      const parser: ParseProduction = this.formGroup.controls['site_data_type'].value === SiteDataInputType.PVSYS ?
        new ParseProductionImplementationPvSys() : new ParseProductionImplementationPvSol();
      this.manager.productionData = [];
      this.csvParser.parseCsvFile(csvFile, this.formGroup.controls['site_data_type'].value === SiteDataInputType.PVSOL ? ';' : '').subscribe({
        next: (result) => {
          this.manager.productionData.push(...parser.parseChunk(result.data));
          result.parser.resume();
        },
        complete: () => {
          const finalData = parser.getFinalData();
          if (finalData) {
            this.manager.productionData.push(finalData);
          }
          this.loadingService.hideLoader();
        },
      });
    });
  }

  addSiteDataUserInputControls() {
    const validators = [Validators.required, Validators.min(0)];
    this.formGroup.addControl('tilt', new FormControl('', validators));
    this.formGroup.addControl('azimuth', new FormControl('', validators));
    this.formGroup.addControl('area', new FormControl('', [Validators.required, Validators.min(1)]));
    this.formGroup.addControl('location', new FormControl(null, validators));
  }
}
